'use client';

import { motion } from 'framer-motion';
import * as React from 'react';

import { useTypewriter } from '@/lib/hooks/useTypewriter';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';

interface SearchInputProps {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onClear?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  /**
   * Real-looking queries typed out one after another in place of the static
   * placeholder. Only while the field is idle — focusing it drops back to
   * `placeholder`, which also stays the accessible name either way.
   */
  placeholders?: string[];
  shortcut?: string;
  /** M — 40px field, S — the header's 32px one (Figma 14629:171374). */
  size?: 'M' | 'S';
  className?: string;
  autoFocus?: boolean;
}

function SearchInput({
  value,
  defaultValue = '',
  onChange,
  onClear,
  onFocus,
  onBlur,
  placeholder = 'Search...',
  placeholders,
  shortcut,
  size = 'M',
  className,
  autoFocus,
}: SearchInputProps) {
  const [internal, setInternal] = React.useState(defaultValue);
  const [focused, setFocused] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement>(null);

  const text = value ?? internal;
  const hasValue = text.length > 0;

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const next = e.target.value;
    setInternal(next);
    onChange?.(next);
  }

  function handleClear() {
    setInternal('');
    onChange?.('');
    onClear?.();
    inputRef.current?.focus();
  }

  function handleContainerClick() {
    inputRef.current?.focus();
  }

  function handleFocus() {
    setFocused(true);
    onFocus?.();
  }

  function handleBlur() {
    setFocused(false);
    onBlur?.();
  }

  const isSmall = size === 'S';
  const textClass = isSmall ? 'text-[12px] leading-[1.15]' : 'text-[13px] leading-[1.5]';
  const showShortcut = Boolean(shortcut) && !focused && !hasValue;
  const showClear = hasValue;
  const isFocused = focused;

  const idle = !focused && !hasValue;
  const suggestions = placeholders ?? EMPTY_SUGGESTIONS;
  // Writes each character straight into the span below — no per-character re-render.
  const typedRef = React.useRef<HTMLSpanElement>(null);
  useTypewriter(typedRef, suggestions, { enabled: idle && suggestions.length > 0 });

  return (
    <div
      onClick={handleContainerClick}
      className={cn(
        'relative flex cursor-text items-center gap-2 rounded-[10px] border border-solid px-[14px] py-[10px]',
        isSmall ? 'h-8' : 'h-[40px]',
        'transition-colors duration-150',
        isFocused ? 'border-v2-bg-blue-50' : 'border-v2-btn-secondary-hover',
        className,
      )}
    >
      {/* Search icon — 20×20 SVG, 16.2 icon inside (Figma inset 9.38%) */}
      <Icon
        name="Search"
        size={isSmall ? '16px' : '20px'}
        className="shrink-0 text-v2-icon-primary"
        aria-hidden
      />

      {/* Input area */}
      <div className="relative flex min-w-0 flex-1 items-center">
        <input
          ref={inputRef}
          type="text"
          autoComplete="off"
          value={text}
          onChange={handleChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          autoFocus={autoFocus}
          className={cn(
            'w-full bg-transparent font-normal outline-none',
            textClass,
            hasValue ? 'text-v2-text-primary' : 'text-v2-text-secondary',
            // hide native placeholder — we render our own only when not focused
            'placeholder-transparent',
          )}
          placeholder={placeholder}
          aria-label={placeholder}
        />
        {/* Placeholder shown only when not focused and empty */}
        {idle && (
          <span
            aria-hidden
            className={cn(
              'pointer-events-none absolute left-0 right-0 select-none overflow-hidden whitespace-nowrap',
              'font-normal text-v2-text-secondary',
              textClass,
            )}
          >
            {suggestions.length > 0 ? (
              <>
                <span ref={typedRef} />
                {/* Caret blinks via motion, not a CSS class: this input ships to
                    the news/trade zones, whose globals.css has no such keyframe. */}
                <motion.span
                  className="ml-px inline-block h-[13px] w-px translate-y-[2px] bg-current"
                  animate={{ opacity: [1, 1, 0, 0] }}
                  transition={{ duration: 1, times: [0, 0.5, 0.5, 1], repeat: Infinity }}
                />
              </>
            ) : (
              placeholder
            )}
          </span>
        )}
      </div>

      {/* Right side: shortcut hint or clear button */}
      {showShortcut && (
        <span className="shrink-0 select-none text-[13px] leading-[1.5] text-v2-text-secondary whitespace-nowrap">
          {shortcut}
        </span>
      )}
      {showClear && (
        /* Cancel button — 20×20 hit area, X path natural size ~11px (Figma inset 23.31%) */
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            handleClear();
          }}
          className="flex shrink-0 size-5 cursor-pointer items-center justify-center text-v2-icon-primary transition-colors duration-150 hover:text-v2-icon-primary-hover outline-none"
          aria-label="Clear search"
        >
          <svg viewBox="0 0 10.678 10.678" width="11" height="11" fill="currentColor" aria-hidden>
            <path d="M9.611.183a.884.884 0 0 1 1.25 0 .884.884 0 0 1 0 1.25L6.222 5.339l4.273 4.272a.884.884 0 0 1-1.25 1.25L4.972 6.589.7 10.861a.884.884 0 0 1-1.25-1.25L3.722 5.339.183 1.067A.884.884 0 0 1 1.433.183L5.339 4.09 9.61.183z" />
          </svg>
        </button>
      )}
    </div>
  );
}

// Stable identity so the typewriter doesn't restart every render.
const EMPTY_SUGGESTIONS: string[] = [];

export type { SearchInputProps };
export { SearchInput };
